<template>
  <CCard class="contract-parties">
    <CCardHeader>
      <div class="section-heading">
        <div>
          <strong>契約に関わる会社</strong>
          <p>
            契約企業・請求先・販売代理店・代行者。
            <b>※この会社は、契約企業が扱う取引先（クライアント）とは異なります。</b>
          </p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <!--
        カードの頭に出すのは、読み込みの失敗だけ（2026-09-26）。
        保存の結果は［保存する］のすぐ下に出す。4社分の欄のあとにボタンがあるので、
        頭に出すと押した所から見えなかった。
      -->
      <CAlert v-if="loadError" color="danger">{{ loadError }}</CAlert>

      <div v-if="loading" class="contract-parties__loading">読み込んでいます…</div>

      <template v-else>
        <CAlert v-if="!linked" color="warning">
          この契約企業は、まだ契約の設定が済んでいません。
          先に契約を保存してから、会社を登録してください。
        </CAlert>

        <!--
          **変えられない人には、欄ごと入力できなくする（2026-09-22）。**
          閲覧の人でも入力できて、保存を押すと初めて断られていた。
          欄を1つずつ止めると足したときに漏れるので、fieldset でまとめて止める。
        -->
        <p v-if="!canEdit" class="text-muted">
          閲覧のみです。契約に関わる会社の変更は、契約情報を更新できる運営の担当者が行います。
        </p>
        <fieldset :disabled="!canEdit" class="contract-parties__fields">
        <section
          v-for="kind in kinds"
          :key="kind.code"
          class="party-block"
        >
          <h3>
            {{ kind.title }}
            <small>{{ kind.note }}</small>
          </h3>

          <div class="party-grid">
            <div>
              <label :for="`name-${kind.code}`">会社名</label>
              <CInput
                :id="`name-${kind.code}`"
                v-model.trim="form[kind.code].company_name"
                maxlength="200"
                :description="kind.emptyNote"
              />
            </div>
            <div>
              <label :for="`staff-${kind.code}`">担当者</label>
              <CInput :id="`staff-${kind.code}`" v-model.trim="form[kind.code].staff_name" maxlength="100"/>
            </div>
            <div>
              <label :for="`mail-${kind.code}`">メール</label>
              <CInput :id="`mail-${kind.code}`" v-model.trim="form[kind.code].email" type="email" maxlength="255"/>
            </div>
            <div>
              <label :for="`tel-${kind.code}`">電話番号</label>
              <CInput :id="`tel-${kind.code}`" v-model.trim="form[kind.code].telephone" maxlength="30"/>
            </div>
            <div>
              <label :for="`zip-${kind.code}`">郵便番号</label>
              <CInput :id="`zip-${kind.code}`" v-model.trim="form[kind.code].postal_code" maxlength="10"/>
            </div>
            <div>
              <label :for="`addr-${kind.code}`">住所</label>
              <CInput :id="`addr-${kind.code}`" v-model.trim="form[kind.code].address" maxlength="255"/>
            </div>
            <div v-if="kind.code === 'BILL_TO' || kind.code === 'CONTRACTOR'">
              <label :for="`inv-${kind.code}`">登録番号</label>
              <CInput
                :id="`inv-${kind.code}`"
                v-model.trim="form[kind.code].invoice_no"
                maxlength="20"
                description="適格請求書発行事業者の登録番号（T＋13桁）"
              />
            </div>
          </div>
        </section>

        <!--
          手数料は**代理店が居るときだけ**出す。
          居ないのに欄があると、直販の契約で「いくら払うのか」を
          考えさせることになる。
        -->
        <section v-if="hasReseller" class="party-block">
          <h3>
            代理店手数料
            <small>※現在は条件の記録のみ行います（計算および支払処理は行われません）。</small>
          </h3>

          <div class="party-grid">
            <div>
              <label for="commission-kind">決め方</label>
              <CSelect
                id="commission-kind"
                :options="commissionKinds"
                :value="commission.commission_kind"
                @update:value="value => { commission.commission_kind = selectedValue(value) }"
              />
            </div>
            <div v-if="commission.commission_kind === 'RATE'">
              <label for="commission-rate">率（%）</label>
              <CInput
                id="commission-rate"
                v-model="commission.rate_percent"
                type="number"
                min="0"
                max="100"
                step="0.01"
                description="0〜100（契約金額に対する割合）"
              />
            </div>
            <div v-if="commission.commission_kind === 'FIXED'">
              <label for="commission-fixed">定額（円／月）</label>
              <CInput
                id="commission-fixed"
                v-model="commission.fixed_amount"
                type="number"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label for="commission-remarks">備考</label>
              <CInput id="commission-remarks" v-model.trim="commission.remarks" maxlength="200"/>
            </div>
          </div>

          <p v-if="commissionSince" class="party-block__since">
            いまの条件は <b>{{ commissionSince }}</b> から。
            保存すると、前の条件は<b>保存せずに閉じる</b>。
          </p>
        </section>

        </fieldset>

        <!--
          **決定はここに1つだけ。**自社設定の編集画面と同じ置き方。
        -->
        <div v-if="canEdit" class="contract-parties__actions">
          <CButton
            type="button"
            color="primary"
            :disabled="saving || !linked"
            @click="save"
          >{{ saving ? '保存しています…' : '保存する' }}</CButton>
        </div>
        <ActionResultMessage :text="successMessage" type="success"/>
        <ActionResultMessage :text="errorMessage" type="danger"/>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import api from '@/services/accessManagement'
import { safeResponseMessage, safeErrorDigest } from '@/utils/safeResponseMessage'
import { selectedValue } from '@/utils/coreuiSelect'
import { BRAND } from '@/common/productName'

/**
 * 契約に関わる会社を出す。
 *
 * <h2>テナントの取引先と混ぜない</h2>
 *
 * 買った会社が扱う取引先（クライアント）と、PREIKAと契約する会社は別のもの。
 * 混ぜると、テナントの取引先一覧に契約企業が出たり、契約企業を消すと
 * テナントの請求書が出せなくなったりする。
 *
 * <h2>販売代理店と代行者を分ける</h2>
 *
 * <b>立場が逆。</b>代理店はうちの代わりに売る側で手数料を払う相手、
 * 代行者はお客様の代わりに手続きする側。ひとつにまとめると
 * 手数料の相手を取り違える。
 */
export default {
  name: 'ContractParties',

  components: { ActionResultMessage },

  props: {
    accountId: { type: Number, required: true },
    /** 契約を変えられるか（CONTRACT_UPDATE）。false なら読むだけ（2026-09-22）。 */
    canEdit: { type: Boolean, default: false }
  },

  data () {
    return {
      kinds: [
        {
          code: 'CONTRACTOR',
          title: '契約企業',
          note: '契約締結・支払を行う会社',
          emptyNote: '空欄にすると登録を削除します。'
        },
        {
          code: 'BILL_TO',
          title: '請求先',
          note: '空欄の場合は契約企業へ請求します',
          emptyNote: '契約企業と同じ場合は、空欄のままで問題ありません。'
        },
        {
          code: 'RESELLER',
          title: '販売代理店',
          note: BRAND + 'の代わりに売った会社。手数料を払う相手',
          emptyNote: '直販なら空のままにします。'
        },
        {
          code: 'AGENT',
          title: '代行者',
          note: 'お客様の代理で手続きを行う会社（社労士・親会社など）',
          emptyNote: '代行が無ければ空のままにします。'
        }
      ],
      form: {},
      commission: { commission_kind: '', rate_percent: '', fixed_amount: '', remarks: '' },
      commissionSince: '',
      commissionKinds: [
        { value: '', label: '（手数料設定なし）' },
        { value: 'RATE', label: '定額率（%）' },
        { value: 'FIXED', label: '定額（円）' }
      ],
      linked: false,
      loading: true,
      saving: false,
      /* 読み込みの失敗（カードの頭に出す）。 */
      loadError: '',
      /* 保存の結果（［保存する］のすぐ下に出す）。 */
      errorMessage: '',
      successMessage: ''
    }
  },

  watch: {
    accountId: {
      immediate: true,
      handler () { this.load() }
    }
  },

  computed: {
    /**
     * 代理店が入っているか。
     *
     * <b>入力中の値で見る。</b>保存前に代理店を入れたら、その場で
     * 手数料の欄が出るほうが、保存し直させるより親切。
     */
    hasReseller () {
      const reseller = this.form.RESELLER
      return !!(reseller && String(reseller.company_name || '').trim())
    }
  },

  methods: {
    selectedValue,

    空の会社 () {
      return {
        company_name: '', staff_name: '', email: '', telephone: '',
        postal_code: '', address: '', building: '', invoice_no: '', remarks: ''
      }
    },

    async load () {
      // 読み込みの通し番号。会社を切り替えたあとに、前の会社の応答で上書きしない（2026-09-22 監査）
      this.loadSeq = (this.loadSeq || 0) + 1
      const mine = this.loadSeq
      const accountId = this.accountId
      const stale = () => mine !== this.loadSeq || accountId !== this.accountId
      this.loading = true
      this.loadError = ''
      this.errorMessage = ''
      this.successMessage = ''
      const form = {}
      this.kinds.forEach(kind => { form[kind.code] = this.空の会社() })
      this.form = form
      try {
        const rows = await api.getContractParties(accountId)
        if (stale()) return
        ;(rows || []).forEach(row => {
          if (!row || !this.form[row.party_kind]) return
          Object.keys(this.空の会社()).forEach(key => {
            this.$set(this.form[row.party_kind], key, row[key] === null || row[key] === undefined ? '' : row[key])
          })
        })
        // **契約へ結ばれているかは、会社が1社でも読めたかでは決められない。**
        // 結ばれていて0社ということもある。保存できるかは口が教える。
        this.linked = true

        const current = await api.getContractCommission(accountId)
        if (stale()) return
        if (current && current.commission_kind) {
          this.commission = {
            commission_kind: current.commission_kind,
            rate_percent: current.rate_percent === null || current.rate_percent === undefined
              ? '' : String(current.rate_percent),
            fixed_amount: current.fixed_amount === null || current.fixed_amount === undefined
              ? '' : String(current.fixed_amount),
            remarks: current.remarks || ''
          }
          this.commissionSince = String(current.valid_from || '').slice(0, 10)
        } else {
          this.commission = { commission_kind: '', rate_percent: '', fixed_amount: '', remarks: '' }
          this.commissionSince = ''
        }
      } catch (error) {
        if (stale()) return
        this.loadError = '契約に関わる会社を読み込めませんでした。'
        this.$emit('load-error', safeErrorDigest(error))
        this.linked = false
      } finally {
        if (!stale()) this.loading = false
      }
    },

    async save () {
      if (!this.canEdit) return
      if (this.saving) return
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      /*
       * **送り先の会社と送る中身は、押した時点で固定する**（2026-09-22 監査）。
       * 1件目を待つ間に別の会社へ切り替えると、2件目だけ別の会社へ、しかも
       * 読み込み途中の空の中身で送られ、その会社の手数料を消すことがあった。
       */
      const accountId = this.accountId
      const partiesBody = {
        account_id: accountId,
        parties: this.kinds.map(kind => Object.assign(
          { party_kind: kind.code }, this.form[kind.code]))
      }
      // **代理店が居るときだけ手数料を送る。**居ないのに送ると、
      // 直販の契約に手数料の約束が残る。
      const commissionBody = {
        account_id: accountId,
        commission_kind: this.hasReseller ? this.commission.commission_kind : '',
        rate_percent: this.commission.commission_kind === 'RATE' && this.commission.rate_percent !== ''
          ? Number(this.commission.rate_percent) : null,
        fixed_amount: this.commission.commission_kind === 'FIXED' && this.commission.fixed_amount !== ''
          ? Number(this.commission.fixed_amount) : null,
        remarks: this.commission.remarks
      }
      try {
        await api.replaceContractParties(partiesBody)
        await api.replaceContractCommission(commissionBody)
        // 保存の途中で別の会社へ切り替えたら、その会社の画面に結果を出さない
        if (accountId !== this.accountId) return

        // **保存できた値を読み直す。**送ったつもりの値ではなく、
        // 入っている値を見せる。
        await this.load()
        if (accountId !== this.accountId) return
        this.successMessage = '契約に関わる会社を保存しました。'
      } catch (error) {
        if (accountId !== this.accountId) return
        this.errorMessage = safeResponseMessage(
          error, '契約に関わる会社を保存できませんでした。時間をおいて再度お試しください。')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.contract-parties__fields {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.contract-parties { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; width: 100%; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }

.contract-parties__loading { color: #5c6873; }

.party-block {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}

.party-block:first-of-type { border-top: 0; padding-top: 0; }

.party-block h3 { font-size: 1rem; font-weight: 600; margin-bottom: 0.5rem; }
.party-block h3 small {
  margin-left: 0.5rem;
  color: #7b8e9b;
  font-weight: 400;
  font-size: 0.75rem;
}

.party-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.75rem;
}

label { display: block; font-weight: 600; font-size: 0.8rem; }

.party-block__since {
  margin: 0.75rem 0 0;
  color: #6a7f8c;
  font-size: 0.8rem;
}

.contract-parties__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}
</style>
